import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Translation, TranslocoLoader } from '@jsverse/transloco';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

/**
 * Loads reg-hub-common's shared translations (/assets/i18n/common/{lang}.json, copied in by angular.json)
 * and the app's own translations (/assets/i18n/{lang}.json), then deep-merges them. App keys win on conflict.
 *
 * Uses HttpBackend so asset requests skip the app's HTTP interceptors (auth headers, error toasts,
 * cancel-on-navigation).
 */
@Injectable({ providedIn: 'root' })
export class RegHubTranslocoLoader implements TranslocoLoader {
  private http = new HttpClient(inject(HttpBackend));

  getTranslation(lang: string): Observable<Translation> {
    // Scoped/lazy requests come in as "scope/lang" - load that file only.
    if (lang.includes('/')) {
      return this.load(`/assets/i18n/${lang}.json`);
    }

    return forkJoin([
      this.load(`/assets/i18n/common/${lang}.json`),
      this.load(`/assets/i18n/${lang}.json`)
    ]).pipe(map(([shared, app]) => deepMerge(shared, app)));
  }

  private load(url: string): Observable<Translation> {
    return this.http.get<Translation>(url).pipe(
      catchError(err => {
        console.error(`Failed to load translations from ${url}`, err);
        return of({} as Translation);
      })
    );
  }
}

function deepMerge(target: Translation, source: Translation): Translation {
  const result: Translation = { ...target };
  for (const key of Object.keys(source)) {
    const s = source[key];
    const t = result[key];
    result[key] = isObject(s) && isObject(t) ? deepMerge(t, s) : s;
  }
  return result;
}

function isObject(value: unknown): value is Translation {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}
